import { API_BASE } from './config';
import { adhocLog } from '../utils/adhocLogger';
import { DISPLAY_TOKEN_KEY } from '../context/authStorage';

/**
 * Thin wrapper around `fetch()` that:
 *  1. Prepends API_BASE to relative paths.
 *  2. Adds `ngrok-skip-browser-warning` header so ngrok free-tier
 *     doesn't return its interstitial HTML page instead of JSON.
 *
 * Use this for every backend call instead of raw `fetch()`.
 */
export function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  const headers = new Headers(init.headers);
  // Tell ngrok to skip the browser warning page for programmatic requests.
  if (!headers.has('ngrok-skip-browser-warning')) {
    headers.set('ngrok-skip-browser-warning', '1');
  }

  const storedToken = localStorage.getItem(DISPLAY_TOKEN_KEY);
  if (storedToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${storedToken}`);
  }

  const method = (init.method ?? 'GET').toUpperCase();
  const isEventIngest = path.includes('/events');
  const start = performance.now();

  if (!isEventIngest) {
    adhocLog('api', 'debug', `${method} ${path} started`);
  }

  return fetch(url, { ...init, headers }).then((res) => {
    if (!isEventIngest) {
      const ms = Math.round(performance.now() - start);
      adhocLog(
        'api',
        res.ok ? 'info' : 'warn',
        `${method} ${path} → ${res.status}`,
        { ms, status: res.status },
      );
    }
    return res;
  }).catch((err) => {
    if (!isEventIngest) {
      const ms = Math.round(performance.now() - start);
      adhocLog('api', 'error', `${method} ${path} failed`, {
        ms,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    throw err;
  });
}
